import { useCallback, useMemo, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { $insertNodes, type TextNode } from 'lexical';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
  type MenuRenderFn,
} from '@lexical/react/LexicalTypeaheadMenuPlugin';
import { T, useGT } from 'gt-react';
import { cn } from '@/lib/utils';
import {
  $textBeforeCaret,
  formulaReferenceTrigger,
  isInsideLookup,
} from './formulaEditorText';
import { FormulaOriginIcon } from './FormulaReferenceChip';
import {
  $createFormulaReferenceNode,
  $createFormulaTableReferenceNode,
} from './formulaReferenceNode';
import { WorkbookItemIcon } from './workbookItemIcon';
import {
  matchingFormulaFields,
  matchingTableColumns,
  type FormulaField,
  type FormulaFields,
  type FormulaTable,
} from './worksheetFormulaFields';

/** One of this Worksheet's Fields, or, inside a lookup, another table's column. */
type PickerEntry =
  | { kind: 'field'; field: FormulaField }
  | { kind: 'table'; table: FormulaTable; column: string };

class FieldOption extends MenuOption {
  constructor(readonly entry: PickerEntry) {
    super(
      entry.kind === 'field'
        ? entry.field.name
        : `${entry.table.id}\u0000${entry.column}`,
    );
  }
}

/** The group an entry is listed under: its origin, or its table. */
function entryGroup(entry: PickerEntry): string {
  return entry.kind === 'field' ? entry.field.origin.kind : entry.table.id;
}

/**
 * Lexical portals the menu's anchor to the body; the menu joins the popover
 * layer there (the z-index and pointer events `@erp/ui` Popover gives its
 * positioner), so the output grid's sticky headers and an open dialog stay
 * beneath it. Positioned, since z-index does nothing on a static box.
 */
const POPOVER_LAYER_STYLE: CSSProperties = {
  zIndex: 1500,
  pointerEvents: 'auto',
};

function useGroupLabel(fields: FormulaFields) {
  const gt = useGT();
  return (entry: PickerEntry) => {
    if (entry.kind === 'table') return entry.table.name;
    const { kind } = entry.field.origin;
    if (kind === 'input') return fields.inputName;
    if (kind === 'lookup')
      return gt('Lookups', { context: 'worksheet formula picker group' });
    return gt('Calculated columns', {
      context: 'worksheet formula picker group',
    });
  };
}

function EntryDetail({ entry }: { entry: PickerEntry }) {
  if (entry.kind === 'table') return null;
  const { field } = entry;
  if (field.origin.kind === 'lookup') return <>{field.origin.sourceName}</>;
  if (field.sample !== undefined) return <>{field.sample}</>;
  return null;
}

function EntryIcon({ entry }: { entry: PickerEntry }) {
  return entry.kind === 'field' ? (
    <FormulaOriginIcon origin={entry.field.origin} size={14} />
  ) : (
    <WorkbookItemIcon
      kind={entry.table.icon}
      aria-hidden
      size={14}
      className="shrink-0"
    />
  );
}

function FormulaReferenceMenu({
  fields,
  container,
  options,
  selectedIndex,
  select,
  highlight,
}: {
  fields: FormulaFields;
  container: HTMLElement;
  options: FieldOption[];
  selectedIndex: number | null;
  select: (option: FieldOption) => void;
  highlight: (index: number) => void;
}) {
  const gt = useGT();
  const groupLabel = useGroupLabel(fields);
  const groups = Map.groupBy(options, (option) => entryGroup(option.entry));
  return createPortal(
    <div
      role="listbox"
      aria-label={gt('Columns to reference', {
        context: 'worksheet formula picker',
      })}
      style={POPOVER_LAYER_STYLE}
      className="relative max-h-72 w-72 overflow-y-auto rounded-md border bg-popover py-1 text-popover-foreground shadow-md"
    >
      {options.length === 0 ? (
        <p className="px-3 py-2 text-sm text-muted-foreground">
          <T context="worksheet formula picker empty">No matching columns</T>
        </p>
      ) : (
        [...groups].map(([group, members]) => (
          <div
            key={group}
            role="group"
            aria-label={members[0] && groupLabel(members[0].entry)}
          >
            <div
              aria-hidden
              className="px-3 pt-2 pb-0.5 text-xs font-semibold text-muted-foreground"
            >
              {members[0] && groupLabel(members[0].entry)}
            </div>
            {members.map((option) => {
              const current = options.indexOf(option);
              return (
                <div
                  key={option.key}
                  id={`typeahead-item-${current}`}
                  ref={(element) => option.setRefElement(element)}
                  role="option"
                  aria-selected={selectedIndex === current}
                  className={cn(
                    'flex cursor-pointer items-center gap-2 px-3 py-1 text-sm select-none',
                    selectedIndex === current && 'bg-accent',
                  )}
                  // Keeps the editor focused so the selection can replace the query.
                  onMouseDown={(event) => {
                    event.preventDefault();
                    select(option);
                  }}
                  onMouseEnter={() => highlight(current)}
                >
                  <EntryIcon entry={option.entry} />
                  <span className="truncate">
                    {option.entry.kind === 'field'
                      ? option.entry.field.name
                      : option.entry.column}
                  </span>
                  <span className="ml-auto truncate font-mono text-xs text-muted-foreground">
                    <EntryDetail entry={option.entry} />
                  </span>
                </div>
              );
            })}
          </div>
        ))
      )}
    </div>,
    container,
  );
}

/**
 * The picker `@`, `[` or `[@` opens: the Fields this Formula may reference,
 * grouped by origin and filtered by what is typed after the trigger, and,
 * inside XLOOKUP, VLOOKUP, SUMIFS or COUNTIFS, the other tables' columns,
 * grouped by table.
 * Enter or Tab inserts the highlighted entry as a chip.
 */
export function FormulaReferencePicker({
  fields,
  onOpenChange,
}: {
  fields: FormulaFields;
  onOpenChange: (open: boolean) => void;
}) {
  const [editor] = useLexicalComposerContext();
  const [query, setQuery] = useState<string | null>(null);
  const [inLookup, setInLookup] = useState(false);
  const options = useMemo(
    () => [
      ...matchingFormulaFields(fields, query ?? '').map(
        (field) => new FieldOption({ kind: 'field', field }),
      ),
      ...(inLookup
        ? matchingTableColumns(fields, query ?? '').map(
            ({ table, column }) =>
              new FieldOption({ kind: 'table', table, column }),
          )
        : []),
    ],
    [fields, query, inLookup],
  );
  const onQueryChange = useCallback(
    (next: string | null) => {
      setQuery(next);
      setInLookup(
        next !== null &&
          editor
            .getEditorState()
            .read(() => isInsideLookup($textBeforeCaret())),
      );
    },
    [editor],
  );
  const onSelectOption = useCallback(
    (
      option: FieldOption,
      queryNode: TextNode | null,
      closeMenu: () => void,
    ) => {
      const { entry } = option;
      const chip =
        entry.kind === 'field'
          ? $createFormulaReferenceNode(entry.field.name)
          : $createFormulaTableReferenceNode(entry.table.id, entry.column);
      if (queryNode) queryNode.replace(chip);
      else $insertNodes([chip]);
      chip.selectNext(0, 0);
      closeMenu();
    },
    [],
  );
  const menuRenderFn: MenuRenderFn<FieldOption> = useCallback(
    (
      anchor,
      {
        options: shown,
        selectedIndex,
        selectOptionAndCleanUp,
        setHighlightedIndex,
      },
    ) =>
      anchor.current ? (
        <FormulaReferenceMenu
          fields={fields}
          container={anchor.current}
          options={shown}
          selectedIndex={selectedIndex}
          select={selectOptionAndCleanUp}
          highlight={setHighlightedIndex}
        />
      ) : null,
    [fields],
  );
  return (
    <LexicalTypeaheadMenuPlugin<FieldOption>
      options={options}
      onQueryChange={onQueryChange}
      onSelectOption={onSelectOption}
      triggerFn={formulaReferenceTrigger}
      menuRenderFn={menuRenderFn}
      onOpen={() => onOpenChange(true)}
      onClose={() => onOpenChange(false)}
    />
  );
}
